import React from 'react';
import { Button, Descriptions, Space, Tag, Typography } from 'antd';
import { formatDate, formatMoney } from '@/utils/format';
import type { PursuitWorkspaceViewModel } from './pursuitWorkspaceModel';

const { Title, Text } = Typography;

export type PursuitHeaderProps = {
  vm: PursuitWorkspaceViewModel;
  primaryActionLabel?: string | null;
  primaryActionDisabled?: boolean;
  primaryActionReason?: string;
  onPrimaryAction?: () => void;
  secondaryLinks?: Array<{ label: string; onClick: () => void }>;
};

export const PursuitHeader: React.FC<PursuitHeaderProps> = ({
  vm,
  primaryActionLabel,
  primaryActionDisabled,
  primaryActionReason,
  onPrimaryAction,
  secondaryLinks,
}) => {
  const s = vm.commercialSummary;
  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start" wrap>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            {s.name}
          </Title>
          <Space wrap size={[8, 8]}>
            <Tag>{s.stageLabel}</Tag>
            {vm.opportunity?.opportunity_number ? (
              <Text type="secondary">{vm.opportunity.opportunity_number}</Text>
            ) : null}
            {vm.context?.lifecycle?.definitionCode ? (
              <Tag color="blue">{vm.context.lifecycle.definitionCode}</Tag>
            ) : null}
          </Space>
        </div>
        <Space wrap>
          {secondaryLinks?.map((l) => (
            <Button key={l.label} onClick={l.onClick}>
              {l.label}
            </Button>
          ))}
          {primaryActionLabel ? (
            <Button
              type="primary"
              disabled={primaryActionDisabled}
              onClick={onPrimaryAction}
              title={primaryActionReason}
              aria-label={primaryActionLabel}
            >
              {primaryActionLabel}
            </Button>
          ) : null}
        </Space>
      </Space>
      <Descriptions
        size="small"
        column={{ xs: 1, sm: 2, md: 3, lg: 4 }}
        style={{ marginTop: 12 }}
        bordered
      >
        <Descriptions.Item label="Client">{s.client}</Descriptions.Item>
        <Descriptions.Item label="Value">
          {s.value != null ? formatMoney(s.value, { currency: s.currency }) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Probability">
          {s.probability != null ? `${s.probability}%` : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Owner">{s.owner}</Descriptions.Item>
        <Descriptions.Item label="Expected close">
          {s.deadline ? formatDate(s.deadline) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Industry">
          {vm.opportunity?.industry || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Project type">
          {vm.opportunity?.project_type || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Status">{vm.opportunity?.status || '—'}</Descriptions.Item>
      </Descriptions>
      {primaryActionDisabled && primaryActionReason ? (
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          {primaryActionReason}
        </Text>
      ) : null}
    </div>
  );
};

export default PursuitHeader;
